import { MutationOptions, useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '~/hooks';
import {
  updateNotificationPreferencesApi,
  updatePrivacyPreferencesApi,
  UpdateNotificationPreferencesData,
  UpdatePrivacyPreferencesData,
} from '../repository';
import type { UserPreferences } from '~/generated/prisma';
import { PREFERENCES_QUERY_KEYS } from '../queries/preferences';

/**
 * Mutation hook for updating notification preferences
 * @param options - Additional mutation options
 * @returns React Query mutation result
 */
export const useUpdateNotificationPreferencesMutation = (
  options?: MutationOptions<
    UserPreferences,
    CustomAxiosError,
    UpdateNotificationPreferencesData
  >,
) => {
  const queryClient = useQueryClient();

  return useApiMutation({
    ...options,
    mutationFn: (data: UpdateNotificationPreferencesData) =>
      updateNotificationPreferencesApi(data),
    onSuccess: async (...rest) => {
      // Invalidate preferences query to refetch updated data
      await queryClient.invalidateQueries({
        queryKey: [PREFERENCES_QUERY_KEYS.PREFERENCES],
      });
      options?.onSuccess?.(...rest);
    },
  });
};

/**
 * Mutation hook for updating privacy preferences
 * @param options - Additional mutation options
 * @returns React Query mutation result
 */
export const useUpdatePrivacyPreferencesMutation = (
  options?: MutationOptions<
    UserPreferences,
    CustomAxiosError,
    UpdatePrivacyPreferencesData
  >,
) => {
  const queryClient = useQueryClient();

  return useApiMutation({
    ...options,
    mutationFn: (data: UpdatePrivacyPreferencesData) =>
      updatePrivacyPreferencesApi(data),
    onSuccess: async (...rest) => {
      // Invalidate preferences query to refetch updated data
      await queryClient.invalidateQueries({
        queryKey: [PREFERENCES_QUERY_KEYS.PREFERENCES],
      });
      options?.onSuccess?.(...rest);
    },
  });
};
